import loadable, { type LoadableComponent } from '@loadable/component';
import React, { Suspense, useState } from 'react';
import { graphql } from 'graphql-schema';
import classNames from 'classnames/bind';
import { skipToken, useSuspenseQuery } from '@apollo/client';
import { detect } from 'detect-browser';
import { useRouteMatch } from 'react-router-dom';
import { dashSpaceSwap } from '../../../utils/url-search-helpers';
import { GenericErrorBoundary, PageLoader, VPAdBanner } from '../../../components';
import { Header } from './Header';
import Hero from './Hero';
import PlayItForward from './PlayItForward';
import VoloApp from './VoloApp';
import Footer from './Footer';
import UseApp from './UseApp';
import styles from './styles.module.scss';
import { StaticUrls } from '../../../shared/static-urls-enum';

const cx = classNames.bind(styles);

const APP_URLS = {
  iOS: StaticUrls.IOS,
  'Android OS': StaticUrls.ANDROID,
} as const;

const SLIDE_DURATION_MS = 5_000;

const BrandLogosBanner = loadable(() => import('./BrandLogosBanner'));
const WhyShouldIPlay = loadable(() => import('./WhyShouldIPlay'));
const WaysToPlay = loadable(() => import('./WaysToPlay'));
// Type cast here because `loadable/component` isn't correctly inferring the props from prop-types
const UpcomingEvents = loadable(() => import('./UpcomingEvents')) as LoadableComponent<{
  cityName: string;
  chosenSport?: string;
}>;
const SignUpForLeague = loadable(() => import('./SignUpForLeague'));
const WhereWePlay = loadable(() => import('./WhereWePlay/WhereWePlay'));
const LeaguePerks = loadable(() => import('./LeaguePerks'));
const Mission = loadable(() => import('./Mission'));
const WaysToPlayWithUs = loadable(() => import('./WaysToPlayWithUs'));

const HOME_PAGE_QUERY = graphql(`
  query HomePageQuery($shouldSkipNationalHeroSlides: Boolean!) {
    organizations(where: { is_discoverable: { _eq: true } }) {
      _id
      name
    }

    national {
      _id
      kids_foundation_stats {
        _id
        number_of_kids_engaged
        programs_completed
        number_of_volunteers_mobilized
        cities_activated
      }

      hero_slides(order_by: { order: asc }) @skip(if: $shouldSkipNationalHeroSlides) {
        _id
        order
        slide_image
        slide_text
        slide_sub_text
        slide_button_text
        slide_link
        new_tab
        secondary_slide_button_text
        secondary_slide_link
        secondary_new_tab
      }
    }
  }
`);

const ORGANIZATION_HERO_QUERY = graphql(`
  query HomePageOrgHero($organizationId: uuid!) {
    organizations_by_pk(_id: $organizationId) {
      _id
      hero_slides(order_by: { order: asc }) {
        _id
        order
        slide_image
        slide_text
        slide_sub_text
        slide_button_text
        slide_link
        new_tab
        secondary_slide_button_text
        secondary_slide_link
        secondary_new_tab
      }
    }
  }
`);

const HomePage: React.FC = () => {
  const { path } = useRouteMatch();

  const cityName = dashSpaceSwap(path.substring(1));

  // handles mobile-only popup if viewing on mobile
  const detected = detect()?.os ?? '';
  const url = detected in APP_URLS ? APP_URLS[detected as keyof typeof APP_URLS] : '';
  const [chosenView, setChosenView] = useState('');

  const { data } = useSuspenseQuery(HOME_PAGE_QUERY, {
    variables: {
      // Do not fetch the national slides if a cityName is specified
      shouldSkipNationalHeroSlides: Boolean(cityName),
    },
  });

  const selectedOrg = data.organizations.find(({ name }) => name === cityName);
  const cityOrgId = selectedOrg?._id;
  const { data: organizationHeroData } = useSuspenseQuery(
    ORGANIZATION_HERO_QUERY,
    cityOrgId
      ? {
          skip: !cityOrgId,
          variables: {
            organizationId: cityOrgId,
          },
        }
      : skipToken
  );

  // fetches hero slides data based on current selected organization
  let heroSlides;

  if (organizationHeroData?.organizations_by_pk) {
    heroSlides = organizationHeroData?.organizations_by_pk?.hero_slides;
  }
  // if no organization is selected or if org has no slides, fetches national hero slides
  else if (data.national[0]?.hero_slides) {
    heroSlides = data.national[0]!.hero_slides;
  }

  const kidsFoundationStats = data.national[0]?.kids_foundation_stats[0];
  if (!kidsFoundationStats) throw new Error('Could not find national data to populate page!');

  const {
    number_of_kids_engaged,
    programs_completed,
    number_of_volunteers_mobilized,
    cities_activated,
  } = kidsFoundationStats;

  return (
    <>
      <div className={cx('App', 'homepage', !!url && !chosenView && 'queryApp')}>
        <Header />
        <Hero
          cityName={cityName}
          heroSlides={
            heroSlides?.map(slide => ({
              newTab: slide.new_tab,
              secondaryNewTab: slide.secondary_new_tab,
              secondarySlideButtonText: slide.secondary_slide_button_text,
              secondarySlideLink: slide.secondary_slide_link,
              slideButtonText: slide.slide_button_text,
              slideImage: slide.slide_image,
              slideLink: slide.slide_link,
              slideSubText: slide.slide_sub_text as string | null,
              slideText: slide.slide_text,
            })) ?? []
          }
          slideDuration={SLIDE_DURATION_MS}
        />
        {!cityName ? ( // show national page
          <>
            <Mission />
            <WaysToPlayWithUs />
            <PlayItForward
              kidsEngaged={number_of_kids_engaged ?? 0}
              programsCompleted={programs_completed ?? 0}
              volunteersMobilized={number_of_volunteers_mobilized ?? 0}
              citiesActivated={cities_activated ?? 0}
            />
            <WhereWePlay />
            <VoloApp />
          </>
        ) : (
          // show city specific page
          <>
            <BrandLogosBanner cityName={cityName} />
            <WaysToPlay cityName={cityName} />
            <WhyShouldIPlay />
            <UpcomingEvents cityName={cityName} />
            <SignUpForLeague cityName={cityName} />
            <PlayItForward
              kidsEngaged={number_of_kids_engaged ?? 0}
              programsCompleted={programs_completed ?? 0}
              volunteersMobilized={number_of_volunteers_mobilized ?? 0}
              citiesActivated={cities_activated ?? 0}
            />
            <LeaguePerks />
            <VoloApp />
          </>
        )}
        <Footer />
      </div>
      {!!url && !chosenView && <UseApp url={url} setChosenView={setChosenView} />}
      <VPAdBanner
        href={cityName ? `/${dashSpaceSwap(cityName, 'toDash')}/volo-pass` : '/volo-pass'}
      />
    </>
  );
};

const HomePageWrapper = () => (
  <GenericErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <HomePage />
    </Suspense>
  </GenericErrorBoundary>
);

export default HomePageWrapper;
